// Piece C of the collaborative-editing follow-up: real editing of a plan
// (SharedWeekEditor) or single day (SharedDayEditor) another household
// shared with you -- replaces the read-only WeekPlanPreview/
// WeekPlanDayPreview on the "Shared with us" page now that
// 0025_week_plan_write_for_household.sql's RPCs make writes work too.
//
// Deliberately NOT a reuse of PlanPage.jsx's day-card rendering -- that
// card carries time-budget pills, nutrition targets, and "recently
// repeated" badges, all derived from the VIEWING household's own
// planDefaults/history, which don't apply when you're looking at someone
// else's plan. This is a simpler, self-contained rendering: day -> meal
// slot -> dish type -> recipe picker, same self-contained shape as
// the plan share sheet (fetches/mutates directly via db.js, not routed
// through MealPlannerContext, since useMealPlannerData's own mutators are
// hard-wired to the signed-in household's own current week).
//
// No Clear day/week, no "Use as template", no share management here --
// those stay owner-only and are simply never offered in this view.
import { useState, useEffect } from "react";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { DAYS, MEAL_SLOTS, PLANNABLE_DISH_TYPES, RECIPE_TYPE_LABELS, DINING_OUT, MISSING_RECIPE_LABEL, COLORS, FONT_SIZE, SPACE, RADIUS, LAYOUT } from "../lib/constants";
import { normalizeDishes } from "../lib/planUtils";
import { RecipeTypeahead } from "./RecipeTypeahead";
import { OpenRecipeLink, RecipeTitleLink } from "./RecipeLinks";
import {
  loadWeekPlanForHouseholdEditable,
  loadWeekPlanDayForHouseholdEditable,
  saveWeekPlanMealForHousehold,
  deleteWeekPlanMealRowForHousehold,
  addWeekPlanItemForHousehold,
  removeWeekPlanItemForHousehold,
  updateWeekPlanMetaForHousehold,
} from "../lib/db";

// Shared by both editors below -- one meal slot's dish-type rows, with the
// same add-row/remove-row/leftover-toggle affordances PlanPage.jsx's day
// card has, minus the servings-shortfall warning and "repeat" badge (both
// need viewer-household context that doesn't apply to someone else's plan).
function MealSlotRow({ slot, dishes, recipes, colors, onSetDish, onAddDishRow, onRemoveDishRow, onToggleLeftover, isLeftover }) {
  const { soft, accent, line, danger } = colors;
  const extraTypes = Object.keys(dishes).filter((k) => k !== "entree");
  const activeTypes = ["entree", ...extraTypes];
  const availableToAdd = PLANNABLE_DISH_TYPES.filter((dt) => dt !== "entree" && !extraTypes.includes(dt));
  const byDish = (dishType) => {
    const options = recipes.filter((r) => r.recipeType === dishType);
    return dishType === "entree" ? [{ id: DINING_OUT, title: "Dining out" }, ...options] : options;
  };

  return (
    <div className="mp-sans">
      <div style={{ fontSize: FONT_SIZE.sm, fontWeight: 700, color: soft, marginBottom: SPACE.sm, textTransform: "capitalize" }}>{slot}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.sm }}>
        {activeTypes.map((dt) => (
          <div key={dt} style={{ display: "flex", alignItems: "center", gap: SPACE.md, flexWrap: "wrap", rowGap: SPACE.sm }}>
            <span style={{ width: LAYOUT.dishLabelWidth, fontSize: FONT_SIZE.xs, color: soft, flexShrink: 0 }}>{RECIPE_TYPE_LABELS[dt]}</span>
            <div style={{ flex: "1 1 140px", minWidth: 0 }}>
              <RecipeTypeahead
                value={dishes[dt]}
                onChange={(id) => onSetDish(dt, id)}
                options={byDish(dt)}
                placeholder={`No ${RECIPE_TYPE_LABELS[dt].toLowerCase()} selected`}
                colors={colors}
              />
            </div>
            {recipes.some((r) => r.id === dishes[dt]) && <OpenRecipeLink recipeId={dishes[dt]} />}
            {dishes[dt] && dishes[dt] !== DINING_OUT && (
              <button
                className="mp-btn"
                onClick={() => onToggleLeftover(dt)}
                title={isLeftover(dt) ? "Marked as leftovers. Click to unmark." : "Mark as leftovers"}
                aria-label={isLeftover(dt) ? "Marked as leftovers. Click to unmark." : "Mark as leftovers"}
                aria-pressed={isLeftover(dt)}
                style={{
                  fontSize: FONT_SIZE.md,
                  lineHeight: 1,
                  padding: "var(--space-xs) var(--space-md)",
                  borderRadius: RADIUS.pill,
                  flexShrink: 0,
                  background: isLeftover(dt) ? COLORS.amberTint : "transparent",
                  border: `1px solid ${isLeftover(dt) ? COLORS.amberBorder : line}`,
                  color: isLeftover(dt) ? COLORS.amberInk : soft,
                }}
              >
                🍱
              </button>
            )}
            {dt !== "entree" && (
              <button className="mp-btn" onClick={() => onRemoveDishRow(dt)} style={{ background: "transparent", color: danger, fontSize: FONT_SIZE.xl, padding: "0 var(--space-xs)", flexShrink: 0 }} aria-label={`Remove ${RECIPE_TYPE_LABELS[dt]}`}>
                ×
              </button>
            )}
          </div>
        ))}
        {availableToAdd.length > 0 && (
          <div style={{ display: "flex", gap: SPACE.sm, flexWrap: "wrap", marginLeft: LAYOUT.dishLabelWidth + SPACE.md }}>
            {availableToAdd.map((dt) => (
              <button key={dt} className="mp-btn" onClick={() => onAddDishRow(dt)} style={{ background: "transparent", border: `1px dashed ${line}`, color: accent, padding: "var(--space-hair) var(--space-md)", borderRadius: RADIUS.pill, fontSize: FONT_SIZE.xs }}>
                + {RECIPE_TYPE_LABELS[dt]}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function SharedWeekEditor({ householdId, weekStart, sections, colors }) {
  const { recipes } = useMealPlanner();
  const { ink, soft, line, danger } = colors;
  const [weekData, setWeekData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    loadWeekPlanForHouseholdEditable(householdId, weekStart)
      .then((wd) => {
        if (!cancelled) setWeekData(wd);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this week.");
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, weekStart]);

  // Debounced, same shape as useMealPlannerData's weekMeta effect -- but
  // scoped to this one foreign week, going through
  // update_week_plan_meta_for_household instead of a plain table upsert.
  useEffect(() => {
    if (!loaded || !weekData) return;
    const t = setTimeout(() => {
      updateWeekPlanMetaForHousehold(householdId, weekStart, { title: weekData.title, servings: weekData.servings, dayBudgets: weekData.dayBudgets }).catch((e) => {
        console.error("[SharedWeekEditor] meta save failed:", e);
        setError("Couldn't save your last change.");
      });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekData?.title, weekData?.servings, loaded, householdId, weekStart]);

  function setDish(day, slot, dishType, recipeId) {
    setWeekData((wd) => {
      const days = { ...wd.days };
      const daySlots = { ...(days[day] || {}) };
      const mealDishes = { ...normalizeDishes(daySlots[slot]) };
      mealDishes[dishType] = recipeId || "";
      daySlots[slot] = mealDishes;
      days[day] = daySlots;
      const leftovers = { ...(wd.leftovers || {}) };
      delete leftovers[`${day}|${slot}|${dishType}`];
      return { ...wd, days, leftovers };
    });
    saveWeekPlanMealForHousehold(householdId, weekStart, day, slot, dishType, recipeId, false).catch((e) => {
      console.error("[SharedWeekEditor] setDish failed:", e);
      setError("Couldn't save that change.");
    });
  }

  function addDishRow(day, slot, dishType) {
    setWeekData((wd) => {
      const days = { ...wd.days };
      const daySlots = { ...(days[day] || {}) };
      const mealDishes = { ...normalizeDishes(daySlots[slot]) };
      if (!(dishType in mealDishes)) mealDishes[dishType] = "";
      daySlots[slot] = mealDishes;
      days[day] = daySlots;
      return { ...wd, days };
    });
    saveWeekPlanMealForHousehold(householdId, weekStart, day, slot, dishType, "", false).catch((e) => {
      console.error("[SharedWeekEditor] addDishRow failed:", e);
      setError("Couldn't save that change.");
    });
  }

  function removeDishRow(day, slot, dishType) {
    setWeekData((wd) => {
      const days = { ...wd.days };
      const daySlots = { ...(days[day] || {}) };
      const mealDishes = { ...normalizeDishes(daySlots[slot]) };
      delete mealDishes[dishType];
      daySlots[slot] = mealDishes;
      days[day] = daySlots;
      const leftovers = { ...(wd.leftovers || {}) };
      delete leftovers[`${day}|${slot}|${dishType}`];
      return { ...wd, days, leftovers };
    });
    deleteWeekPlanMealRowForHousehold(householdId, weekStart, day, slot, dishType).catch((e) => {
      console.error("[SharedWeekEditor] removeDishRow failed:", e);
      setError("Couldn't save that change.");
    });
  }

  function toggleLeftover(day, slot, dishType) {
    const key = `${day}|${slot}|${dishType}`;
    const currentRecipeId = weekData.days[day]?.[slot]?.[dishType] || "";
    const nextValue = !weekData.leftovers?.[key];
    setWeekData((wd) => {
      const leftovers = { ...(wd.leftovers || {}) };
      if (leftovers[key]) delete leftovers[key];
      else leftovers[key] = true;
      return { ...wd, leftovers };
    });
    saveWeekPlanMealForHousehold(householdId, weekStart, day, slot, dishType, currentRecipeId, nextValue).catch((e) => {
      console.error("[SharedWeekEditor] toggleLeftover failed:", e);
      setError("Couldn't save that change.");
    });
  }

  const ITEM_FIELDS = { snack: "snacks", preserves: "preserves", medicinal: "medicinal" };

  async function addItem(listType, recipeId) {
    if (!recipeId) return;
    const field = ITEM_FIELDS[listType];
    try {
      const itemId = await addWeekPlanItemForHousehold(householdId, weekStart, listType, recipeId);
      setWeekData((wd) => ({ ...wd, [field]: [...(wd[field] || []), { id: itemId, recipeId }] }));
    } catch (e) {
      console.error("[SharedWeekEditor] addItem failed:", e);
      setError("Couldn't save that change.");
    }
  }

  async function removeItem(listType, index) {
    const field = ITEM_FIELDS[listType];
    const entry = weekData[field]?.[index];
    if (!entry) return;
    setWeekData((wd) => ({ ...wd, [field]: (wd[field] || []).filter((e) => e.id !== entry.id) }));
    try {
      await removeWeekPlanItemForHousehold(entry.id);
    } catch (e) {
      console.error("[SharedWeekEditor] removeItem failed:", e);
      setError("Couldn't save that change.");
    }
  }

  if (!loaded) return <div style={{ color: soft, fontSize: FONT_SIZE.base }}>Loading…</div>;
  if (!weekData) return <div style={{ color: soft, fontSize: FONT_SIZE.base }}>Couldn't load this week.</div>;

  const itemSections = [
    ["snack", "snacks", "Snacks/Treats", sections?.includeSnacks],
    ["preserves", "preserves", "Preserves", sections?.includePreserves],
    ["medicinal", "medicinal", "Medicinal", sections?.includeMedicinal],
  ];

  return (
    <div className="mp-sans">
      <div style={{ display: "flex", gap: SPACE.lg, alignItems: "center", marginBottom: SPACE.xl, flexWrap: "wrap" }}>
        <input
          type="text"
          value={weekData.title || ""}
          onChange={(e) => setWeekData((wd) => ({ ...wd, title: e.target.value }))}
          placeholder="Week title"
          style={{ flex: 1, minWidth: 160, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, fontSize: FONT_SIZE.base, color: ink }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: SPACE.sm, fontSize: FONT_SIZE.sm, color: soft }}>
          Servings
          <input
            type="number"
            min="1"
            value={weekData.servings ?? 4}
            onChange={(e) => setWeekData((wd) => ({ ...wd, servings: Math.max(1, Number(e.target.value) || 1) }))}
            style={{ width: 56, padding: "var(--space-xs) var(--space-md)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, fontSize: FONT_SIZE.base, color: ink }}
          />
        </label>
      </div>
      {error && <div style={{ color: danger, fontSize: FONT_SIZE.sm, marginBottom: SPACE.lg }}>{error}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.lg }}>
        {DAYS.map((day) => (
          <div key={day} style={{ border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl)" }}>
            <div style={{ fontWeight: 700, fontSize: FONT_SIZE.base, marginBottom: SPACE.md }}>{day}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: SPACE.lg }}>
              {MEAL_SLOTS.map((slot) => (
                <MealSlotRow
                  key={slot}
                  slot={slot}
                  dishes={normalizeDishes(weekData.days[day]?.[slot])}
                  recipes={recipes}
                  colors={colors}
                  onSetDish={(dt, id) => setDish(day, slot, dt, id)}
                  onAddDishRow={(dt) => addDishRow(day, slot, dt)}
                  onRemoveDishRow={(dt) => removeDishRow(day, slot, dt)}
                  onToggleLeftover={(dt) => toggleLeftover(day, slot, dt)}
                  isLeftover={(dt) => !!weekData.leftovers?.[`${day}|${slot}|${dt}`]}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {itemSections.map(
        ([listType, field, label, included]) =>
          included && (
            <div key={listType} style={{ marginTop: SPACE.xxl, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl)" }}>
              <div style={{ fontWeight: 700, fontSize: FONT_SIZE.base, marginBottom: SPACE.md }}>{label}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: SPACE.sm, marginBottom: SPACE.lg }}>
                {(weekData[field] || []).map((entry, i) => {
                  const r = recipes.find((x) => x.id === entry.recipeId);
                  return (
                    <div key={entry.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: FONT_SIZE.base, background: COLORS.card, border: `1px solid ${line}`, borderRadius: RADIUS.md, padding: "var(--space-xs) var(--space-md)", gap: SPACE.md }}>
                      <span>{r ? <RecipeTitleLink recipeId={r.id} title={r.title} /> : MISSING_RECIPE_LABEL}</span>
                      <button className="mp-btn" onClick={() => removeItem(listType, i)} style={{ background: "transparent", color: danger, fontSize: FONT_SIZE.xl, padding: "0 var(--space-xs)" }} aria-label={`Remove ${label}`}>
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              <RecipeTypeahead
                value=""
                onChange={(id) => addItem(listType, id)}
                options={recipes.filter((r) => (listType === "snack" ? (r.mealTypes || []).includes("snack") || r.recipeType === "candy" : r.recipeType === listType))}
                placeholder={`+ Add ${label.toLowerCase()}`}
                colors={colors}
              />
            </div>
          )
      )}
    </div>
  );
}

export function SharedDayEditor({ householdId, weekStart, day, colors }) {
  const { recipes } = useMealPlanner();
  const { soft, danger } = colors;
  const [dayData, setDayData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    loadWeekPlanDayForHouseholdEditable(householdId, weekStart, day)
      .then((dd) => {
        if (!cancelled) setDayData(dd);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this day.");
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, weekStart, day]);

  function setDish(slot, dishType, recipeId) {
    setDayData((dd) => {
      const slots = { ...dd.slots };
      const mealDishes = { ...normalizeDishes(slots[slot]) };
      mealDishes[dishType] = recipeId || "";
      slots[slot] = mealDishes;
      const leftovers = { ...(dd.leftovers || {}) };
      delete leftovers[`${slot}|${dishType}`];
      return { ...dd, slots, leftovers };
    });
    saveWeekPlanMealForHousehold(householdId, weekStart, day, slot, dishType, recipeId, false).catch((e) => {
      console.error("[SharedDayEditor] setDish failed:", e);
      setError("Couldn't save that change.");
    });
  }

  function addDishRow(slot, dishType) {
    setDayData((dd) => {
      const slots = { ...dd.slots };
      const mealDishes = { ...normalizeDishes(slots[slot]) };
      if (!(dishType in mealDishes)) mealDishes[dishType] = "";
      slots[slot] = mealDishes;
      return { ...dd, slots };
    });
    saveWeekPlanMealForHousehold(householdId, weekStart, day, slot, dishType, "", false).catch((e) => {
      console.error("[SharedDayEditor] addDishRow failed:", e);
      setError("Couldn't save that change.");
    });
  }

  function removeDishRow(slot, dishType) {
    setDayData((dd) => {
      const slots = { ...dd.slots };
      const mealDishes = { ...normalizeDishes(slots[slot]) };
      delete mealDishes[dishType];
      slots[slot] = mealDishes;
      const leftovers = { ...(dd.leftovers || {}) };
      delete leftovers[`${slot}|${dishType}`];
      return { ...dd, slots, leftovers };
    });
    deleteWeekPlanMealRowForHousehold(householdId, weekStart, day, slot, dishType).catch((e) => {
      console.error("[SharedDayEditor] removeDishRow failed:", e);
      setError("Couldn't save that change.");
    });
  }

  function toggleLeftover(slot, dishType) {
    const key = `${slot}|${dishType}`;
    const currentRecipeId = dayData.slots[slot]?.[dishType] || "";
    const nextValue = !dayData.leftovers?.[key];
    setDayData((dd) => {
      const leftovers = { ...(dd.leftovers || {}) };
      if (leftovers[key]) delete leftovers[key];
      else leftovers[key] = true;
      return { ...dd, leftovers };
    });
    saveWeekPlanMealForHousehold(householdId, weekStart, day, slot, dishType, currentRecipeId, nextValue).catch((e) => {
      console.error("[SharedDayEditor] toggleLeftover failed:", e);
      setError("Couldn't save that change.");
    });
  }

  if (!loaded) return <div style={{ color: soft, fontSize: FONT_SIZE.base }}>Loading…</div>;
  if (!dayData) return <div style={{ color: soft, fontSize: FONT_SIZE.base }}>Couldn't load this day.</div>;

  return (
    <div className="mp-sans">
      {error && <div style={{ color: danger, fontSize: FONT_SIZE.sm, marginBottom: SPACE.lg }}>{error}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.lg }}>
        {MEAL_SLOTS.map((slot) => (
          <MealSlotRow
            key={slot}
            slot={slot}
            dishes={normalizeDishes(dayData.slots[slot])}
            recipes={recipes}
            colors={colors}
            onSetDish={(dt, id) => setDish(slot, dt, id)}
            onAddDishRow={(dt) => addDishRow(slot, dt)}
            onRemoveDishRow={(dt) => removeDishRow(slot, dt)}
            onToggleLeftover={(dt) => toggleLeftover(slot, dt)}
            isLeftover={(dt) => !!dayData.leftovers?.[`${slot}|${dt}`]}
          />
        ))}
      </div>
    </div>
  );
}
